import { RevenueValue } from "@/components/RevenueEye";
import { useEffect, useState } from "react";
import { FEATURES } from "@/lib/features";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CountersignQueue } from "@/components/CountersignQueue";
import { useAuth } from "@/context/AuthContext";
import { TeamWorkloadCard } from "@/components/TeamWorkloadCard";
import { ImportantItemsCard } from "@/components/ImportantItemsCard";
import { WelcomeBanner } from "@/components/WelcomeBanner";
import { Loader, Card } from "@/components/ui-helpers";
import {
  UserPlus, CalendarClock, FolderKanban, FileText, CheckSquare, CalendarDays,
  Wallet, TrendingUp, Activity as ActivityIcon, CircleDollarSign,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid,
} from "recharts";

const RANGES = [["today", "Today"], ["week", "This Week"], ["month", "This Month"]];
const PIE = ["#1479C4", "#F5822A", "#5A34B4", "#0EA5A5", "#1E9BE9", "#EAB308", "#7C3AED", "#0891B2"];

const ACTION_LABEL = {
  login: "signed in", lead_create: "added a lead", lead_status_change: "updated a lead",
  lead_convert: "converted a lead", client_create: "created a client", client_update: "updated a client",
  application_create: "opened a case", application_stage_change: "moved a case stage",
  document_upload: "uploaded a document", document_review: "reviewed a document",
  task_create: "created a task", invoice_create: "raised an invoice", payment_record: "recorded a payment",
  appointment_create: "booked an appointment", staff_create: "added a staff member",
};

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [range, setRange] = useState("month");

  const load = () => api.get(`/dashboard/admin?range=${range}`).then((r) => setData(r.data)).catch(() => setData({}));
  useEffect(() => { setData(null); load(); }, [range]); // eslint-disable-line

  const kpis = data ? [
    { label: "New Leads", value: data.new_leads, icon: UserPlus, to: "/admin/leads", accent: "text-blue-600 bg-blue-50" },
    { label: "Follow-ups Due", value: data.followups_due, icon: CalendarClock, to: "/admin/leads", accent: "text-amber-600 bg-amber-50" },
    { label: "Active Cases", value: data.active_cases, icon: FolderKanban, to: "/admin/applications", accent: "text-primary bg-primary/10" },
    ...(FEATURES.documents ? [{ label: "Documents Pending", value: data.documents_pending_review, icon: FileText, to: "/admin/applications", accent: "text-violet-600 bg-violet-50" }] : []),
    { label: "Tasks Due Today", value: data.tasks_due_today, icon: CheckSquare, to: "/admin/applications", accent: "text-rose-600 bg-rose-50" },
    { label: "Upcoming Appointments", value: data.upcoming_appointments, icon: CalendarDays, to: "/admin/appointments", accent: "text-teal-600 bg-teal-50" },
    { label: user?.role === "admin" ? "Total Pending Fees" : "Pending Fees", value: `$${(data.outstanding_payments || 0).toLocaleString()}`, icon: Wallet, to: "/admin/billing", accent: "text-orange-600 bg-orange-50", sub: "Open + closed cases" },
    { label: "Short-Term Courses Balance", value: `$${(data.short_term_outstanding || 0).toLocaleString()}`, icon: Wallet, to: "/admin/short-term", accent: "text-sky-600 bg-sky-50", sub: "Separate from case fees" },
    { label: "Fees Pending (Closed Cases)", value: `$${(data.fees_pending_total || 0).toLocaleString()}`, icon: CircleDollarSign, to: "/admin/applications?fees_pending=1", accent: "text-red-600 bg-red-50",
      sub: data.fees_pending_count ? `${data.fees_pending_count} case${data.fees_pending_count === 1 ? "" : "s"} · oldest ${data.fees_pending_oldest_days || 0} day${data.fees_pending_oldest_days === 1 ? "" : "s"}` : "Nothing owing" },
    ...(data.can_see_revenue ? [{ label: "Revenue (period)", value: <RevenueValue value={`$${(data.range_revenue || 0).toLocaleString()}`} testid="dashboard-revenue" />, icon: TrendingUp, to: "/admin/reports", accent: "text-emerald-600 bg-emerald-50" }] : []),
  ].filter(Boolean) : [];

  return (
    <div>
      <WelcomeBanner />
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="font-head text-2xl sm:text-3xl font-extrabold text-slate-900">Welcome back, {(user?.name || "").split(" ")[0]}</h1>
          <p className="text-slate-500 text-sm mt-1">{user?.role === "staff" ? "Here's your own cases, clients and tasks today." : "Here's what's happening across Chahal Overseas today."}</p>
        </div>
        <div className="inline-flex bg-slate-100 rounded-xl p-1" data-testid="range-tabs">
          {RANGES.map(([k, l]) => (
            <button key={k} data-testid={`range-${k}`} onClick={() => setRange(k)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${range === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>{l}</button>
          ))}
        </div>
      </div>
      {(user?.role === "admin" || (user?.permissions || []).includes("manage_settings")) && <CountersignQueue />}

      {!data ? <Loader /> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {kpis.map((k) => (
              <Link key={k.label} to={k.to} data-testid={`kpi-${k.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}>
                <Card className="p-4 sm:p-5 hover:shadow-md transition-shadow h-full">
                  <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${k.accent}`}><k.icon className="h-5 w-5" /></div>
                  <div className="text-2xl font-head font-extrabold text-slate-900 mt-3">{k.value}</div>
                  <div className="text-xs font-medium text-slate-500 mt-0.5">{k.label}</div>
                  {k.sub && <div className="text-[11px] text-slate-400 mt-0.5" data-testid="kpi-fees-pending-sub">{k.sub}</div>}
                </Card>
              </Link>
            ))}
          </div>

          <ImportantItemsCard cases={data.important_cases} tasks={data.important_tasks} />
          {data.staff_workload?.length > 0 && <TeamWorkloadCard rows={data.staff_workload} onChanged={load} />}

          <div className="grid lg:grid-cols-3 gap-4 mt-4">
            <Card className="p-5 lg:col-span-2">
              <h3 className="font-head font-bold text-slate-900 mb-4">Cases by Service</h3>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.by_service || []}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#EEF2F6" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={60} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#1479C4" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>

            <Card className="p-5">
              <h3 className="font-head font-bold text-slate-900 mb-4">Leads by Status</h3>
              {(data.leads_by_status || []).length === 0 ? (
                <div className="text-sm text-slate-400 py-10 text-center">No leads yet</div>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={data.leads_by_status} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={(e) => e.name}>
                      {data.leads_by_status.map((_, i) => <Cell key={i} fill={PIE[i % PIE.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Card>
          </div>

          {data.can_view_audit && (
          <Card className="p-5 mt-4">
            <h3 className="font-head font-bold text-slate-900 mb-4 flex items-center gap-2"><ActivityIcon className="h-4 w-4 text-primary" /> Recent Activity</h3>
            {(data.recent_activity || []).length === 0 ? (
              <div className="text-sm text-slate-400 py-6 text-center">No recent activity</div>
            ) : (
              <ul className="divide-y divide-slate-100 max-h-[460px] overflow-y-auto" data-testid="dashboard-recent-activity">
                {data.recent_activity.map((a) => (
                  <li key={a.id} className="py-2.5 flex items-center gap-3 text-sm">
                    <div className="h-8 w-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0 text-xs font-bold text-slate-600">{(a.user_name || "?").charAt(0)}</div>
                    <div className="min-w-0 flex-1">
                      <span className="font-semibold text-slate-800">{a.user_name || "Someone"}</span>
                      <span className="text-slate-500"> {ACTION_LABEL[a.action] || a.action.replace(/_/g, " ")}</span>
                      {a.detail && <span className="text-slate-400"> · {a.detail}</span>}
                    </div>
                    <span className="text-xs text-slate-400 shrink-0">{timeAgo(a.created_at)}</span>
                  </li>
                ))}
              </ul>
            )}
            <Link to="/admin/reports" data-testid="dashboard-activity-viewall" className="inline-block mt-3 text-xs font-semibold text-primary hover:underline">View full activity log →</Link>
          </Card>
          )}
        </>
      )}
    </div>
  );
}
